'use client';

import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

const isSecure =
  typeof window !== 'undefined' && window.location.protocol === 'https:';

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const [name, ...rest] = c.trim().split('=');
          return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
        })
        .filter((c) => c.name);

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=Lax`;
  if (isSecure) s += '; Secure';
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${isSecure ? '; Secure' : ''}`;
};

// Clear all Supabase auth cookies to prevent stale token retry loops
const clearAuthCookies = () => {
  if (typeof document === 'undefined') return;
  fromCookies()
    .filter((c) => c.name.includes('supabase') || c.name.includes('sb-'))
    .forEach((c) => deleteCookie(c.name));
};

const getToken = () =>
  fromCookies().find((c) => c.name.includes('auth-token'))?.value ?? null;

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getToken();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : (input as Request).url;
    if (token && (url.startsWith('/') || url.startsWith(window.location.origin))) {
      init = {
        ...(init || {}),
        headers: { ...(init?.headers || {}), 'x-sb-token': token },
      };
    }
    return orig(input, init);
  };
}

// Singleton instance — prevents multiple clients competing for the same
// navigator lock, which causes "Lock broken by another request with the
// 'steal' option" AbortErrors in React Strict Mode / concurrent renders.
let _client: SupabaseClient | null = null;

// Reset the singleton (called when a fatal auth error is detected)
export function resetClient(): void {
  _client = null;
}

export function createClient(): SupabaseClient {
  if (_client) return _client;

  _client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => fromCookies(),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) =>
            value ? setCookie(name, value, options) : deleteCookie(name)
          );
        },
      },
      auth: {
        // Disable the Web Locks API for auth token management.
        // The navigator lock causes AbortErrors when multiple concurrent
        // calls (React Strict Mode double-invoke, multiple tabs) try to
        // acquire the same lock. Using a no-op lock function is the
        // recommended workaround from the Supabase team.
        lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => fn(),
      },
      global: {
        fetch: async (url, options) => {
          const response = await fetch(url, options);
          // If Supabase returns a rate-limit or bad-token response, clear
          // cookies so the client stops retrying with the stale token.
          if (response.status === 429 || response.status === 400) {
            const cloned = response.clone();
            try {
              const body = await cloned.json();
              const code: string = body?.error_code ?? body?.code ?? '';
              const msg: string = (body?.error_description ?? body?.msg ?? body?.message ?? '').toLowerCase();
              const isFatal =
                code === 'refresh_token_already_used' ||
                code === 'over_request_rate_limit' ||
                code === 'invalid_refresh_token'|| msg.includes('already used') ||
                msg.includes('rate limit') ||
                msg.includes('invalid refresh token');
              if (isFatal) {
                clearAuthCookies();
                // Also clear localStorage to stop the client from retrying with stale tokens
                try {
                  Object.keys(localStorage).forEach((key) => {
                    if (key.startsWith('sb-') || key.includes('supabase')) {
                      localStorage.removeItem(key);
                    }
                  });
                } catch (_) {
                  // localStorage not available
                }
                // Reset singleton so next createClient() call gets a fresh instance
                // _client = null; ← REMOVED: resetting the singleton here caused an
                // infinite auth error loop. The singleton must persist for the
                // lifetime of the page; clearing cookies is sufficient to stop retries.
              }
            } catch {
              // ignore JSON parse errors
            }
          }
          return response;
        },
      },
    }
  );

  return _client;
}
